import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { useId } from 'react';

import { type Organization, client } from '../../api.js';
import { getQueryProps } from '../../hooks/index.js';
import { FormField } from '../Form/FormField';

interface Props {
  name: string;
  label: string;
  helperText?: string;
  disabled?: boolean;
  isRequired?: boolean;
  onChange?: (value: string | undefined) => Promise<void>;
}

export function OrganizationAutocompleteControl(props: Props) {
  const { name, label, helperText, disabled, isRequired, onChange } = props;

  const { data: organizations = [], isLoading } = useQuery(
    getQueryProps(client.Organization, 'findAll')
  );

  const [field, { error, touched }, { setValue }] = useField<string | undefined>(name);

  const fieldId = useId();
  const selectedValue =
    organizations.find((organization) => organization.id === field.value) ?? field.value;

  return (
    <FormField
      id={fieldId}
      label={label}
      helperText={helperText}
      required={isRequired}
      error={touched ? error : undefined}
    >
      <Autocomplete<Organization.Organization | string>
        id={fieldId}
        loading={isLoading}
        value={selectedValue ?? null} // mui needs null, not undefined
        disabled={disabled}
        multiple={false}
        getOptionLabel={(organizationOrId) => {
          if (typeof organizationOrId === 'string') {
            return organizationOrId.length > 0 ? `Unknown Id: ${organizationOrId}` : '';
          }
          return organizationOrId.name;
        }}
        onChange={async (_, organizationOrId) => {
          const organizationId =
            typeof organizationOrId === 'string'
              ? organizationOrId
              : (organizationOrId?.id ?? undefined);
          // formik needs undefined, not null
          void setValue(organizationId);
          await onChange?.(organizationId);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            inputProps={{ ...params.inputProps, 'aria-required': isRequired }}
          />
        )}
        options={organizations}
        sx={{
          minWidth: '320px',
        }}
      />
    </FormField>
  );
}
